import PropTypes from 'prop-types';

import { I18n } from 'Locales';

import signatureStyle from './Signature/style';

const bankComponentOnlineFields = ['accountNumber', 'sortCode', 'registrationNumber', 'holderName'];

const componentPropTypes = {
  current_time: PropTypes.string,
  description: PropTypes.string,
  fields: PropTypes.array,
  id: PropTypes.string,
  logic_rules: PropTypes.array,
  name: PropTypes.string,
  properties: PropTypes.array,
  relationshipNames: PropTypes.array,
  shouldShowError: PropTypes.bool,
  type: PropTypes.string,
  updateValue: PropTypes.func
};

const getFieldProp = (fields, name) => fields.filter(f => f.field_type === name)[0];

const getIconColor = (fieldHasError, hasFieldValue) => (fieldHasError ? 'red' : hasFieldValue ? 'blue' : 'gray');

const CURRENCY_BY_COUNTRY = { US: 'USD', UK: 'GBP', IE: 'EUR', FR: 'EUR', BE: 'EUR', DE: 'EUR', LU: 'EUR' };

const NO_DISCOUNT = { discountAmount: 0, matchedRule: null, negativeTotal: false };

// Reads a saved JSON string (or array) and always returns an array.
const readJsonArray = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatCurrency = (amount, flowCountry) => {
  const currency = CURRENCY_BY_COUNTRY[flowCountry] || 'EUR';
  return (
    new Intl.NumberFormat(I18n.language, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol'
    })
      .format(parseFloat(amount || 0))
      // Client requirement: no space between a leading currency symbol and the amount (€10,00).
      .replace(/(\p{Sc})\s/u, '$1')
  );
};

const addProductDetails = (rawLines, referencedProducts) => {
  if (!rawLines || rawLines.length === 0) return [];

  const productById = new Map((referencedProducts || []).map(p => [p.id, p]));

  return rawLines
    .map(line => {
      const product = productById.get(line.product_id);

      // Known catalog product: take name/price from the catalog.
      if (product) {
        return {
          product_id: line.product_id,
          reference_number: product.reference_number,
          name: product.name,
          unit_price: parseFloat(product.value || 0),
          quantity: parseInt(line.quantity) || 1
        };
      }

      // Custom/legacy line: keep the details it already carries.
      if (line.name || line.reference_number) {
        return {
          product_id: line.product_id,
          reference_number: line.reference_number || '',
          name: line.name || '',
          unit_price: parseFloat(line.unit_price || line.value || line.price || 0),
          quantity: parseInt(line.quantity) || 1
        };
      }

      // Nothing to show.
      return null;
    })
    .filter(Boolean);
};

const getCartLines = (componentFlowData, value, referencedProducts) => {
  const rawLines = componentFlowData?.lines?.length > 0 ? componentFlowData.lines : readJsonArray(value);
  return addProductDetails(rawLines, referencedProducts);
};

const calculateSubtotal = lines =>
  (lines || []).reduce((sum, line) => sum + line.quantity * parseFloat(line.unit_price || 0), 0);

// An empty/invalid min_subtotal means "no minimum" (0); an empty/invalid
// max_subtotal means "no ceiling" (Infinity).
const getRuleMin = rule => {
  const min = parseFloat(rule.min_subtotal);
  return Number.isFinite(min) ? min : 0;
};

const getRuleMax = rule => {
  const max = parseFloat(rule.max_subtotal);
  return Number.isFinite(max) ? max : Infinity;
};

const resolveDiscount = (discountRules, subtotal) => {
  if (!discountRules || discountRules.length === 0 || subtotal <= 0) {
    return NO_DISCOUNT;
  }

  let matchedRule = discountRules.find(rule => subtotal >= getRuleMin(rule) && subtotal <= getRuleMax(rule));

  if (!matchedRule) {
    const applicableRules = discountRules
      .filter(rule => subtotal >= getRuleMin(rule))
      .sort((a, b) => getRuleMin(b) - getRuleMin(a));
    matchedRule = applicableRules[0] || null;
  }

  if (!matchedRule) return NO_DISCOUNT;

  // Subtotal beyond this rule's band still uses the rule, but the discount is
  // capped as if the subtotal were the rule's max_subtotal.
  const discountBase = Math.min(subtotal, getRuleMax(matchedRule));

  let discountAmount = 0;
  if (matchedRule.discount_kind === 'fixed') {
    discountAmount = parseFloat(matchedRule.discount_value);
  } else if (matchedRule.discount_kind === 'percent') {
    discountAmount = Math.round(((discountBase * parseFloat(matchedRule.discount_value)) / 100) * 100) / 100;
  }

  if (!Number.isFinite(discountAmount) || discountAmount < 0) {
    return NO_DISCOUNT;
  }

  if (subtotal - discountAmount < 0) {
    return { discountAmount: 0, matchedRule: null, negativeTotal: true };
  }

  return { discountAmount, matchedRule, negativeTotal: false };
};

// Totals for the summary view. Uses the values saved with the cart, and only
// recomputes from the lines for older data that was saved without totals.
const computeCartTotals = (lines, componentFlowData = {}) => {
  const subtotal = parseFloat(componentFlowData.subtotal) || calculateSubtotal(lines);
  const discountAmount = parseFloat(componentFlowData.discount_amount) || 0;
  const finalTotal = parseFloat(componentFlowData.final_total) || Math.max(0, subtotal - discountAmount);

  return { subtotal, discountAmount, finalTotal };
};

const getDiscountRules = discountRulesRaw => readJsonArray(discountRulesRaw);

export {
  bankComponentOnlineFields,
  calculateSubtotal,
  componentPropTypes,
  computeCartTotals,
  formatCurrency,
  getCartLines,
  getDiscountRules,
  getFieldProp,
  getIconColor,
  readJsonArray,
  resolveDiscount,
  signatureStyle
};
